import { Box } from '@mui/material';
import classNames from 'classnames/bind';
import { memo, useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import styles from '../Report.module.scss';
import { personCccd, personFullName, personMenuLabel, personRowId } from './personSubjectFilter';
import { useReportSubjectPersons } from './useReportSubjectPersons';

const cx = classNames.bind(styles);

type PersonSelectOption = {
  id: string;
  name: string;
  fullName: string;
  cccd: string;
};

export type ReportSubjectFilterProps = {
  username?: string;
  triggerId?: string;
  selectedPersonId: string | null;
  onSelectedPersonIdChange: (personId: string | null) => void;
  onMenuOpenChange?: (open: boolean) => void;
  onListReadyChange?: (ready: boolean, count: number) => void;
};

const ReportSubjectFilter = memo(function ReportSubjectFilter({
  username,
  triggerId,
  selectedPersonId,
  onSelectedPersonIdChange,
  onMenuOpenChange,
  onListReadyChange,
}: ReportSubjectFilterProps) {
  const { t } = useTranslation('admin/report');

  const [searchInput, setSearchInput] = useState('');

  const { subjectPersonsDisplayed, resolvePersonLabel, listReady, personsCount } = useReportSubjectPersons(
    username,
    searchInput
  );

  useEffect(() => {
    onListReadyChange?.(listReady, personsCount);
  }, [listReady, personsCount, onListReadyChange]);

  const personOptions = useMemo<PersonSelectOption[]>(() => {
    return subjectPersonsDisplayed
      .map((p) => {
        const id = personRowId(p);
        const fullName = personFullName(p);
        const cccd = personCccd(p);
        const name = personMenuLabel(p);
        if (!id || !name) return null;
        return { id, name, fullName, cccd };
      })
      .filter((item): item is PersonSelectOption => item !== null);
  }, [subjectPersonsDisplayed]);

  const renderPersonOptionRow = useCallback((option: PersonSelectOption) => {
    const displayName = option.fullName || option.name;
    return (
      <Box className={cx('report-subject-option-row', 'select-option', 'w-full', 'flex')}>
        <span className={cx('report-subject-option-name', 'text-base')}>{displayName}</span>
        {option.cccd ? <span className={cx('report-subject-option-cccd', 'text-base')}>{option.cccd}</span> : null}
      </Box>
    );
  }, []);

  const selectedLabel = useMemo(() => {
    if (!selectedPersonId) return undefined;
    return resolvePersonLabel(selectedPersonId);
  }, [selectedPersonId, resolvePersonLabel]);

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearchInput(event.target.value);
  }, []);

  const handleMenuOpen = useCallback(() => {
    onMenuOpenChange?.(true);
  }, [onMenuOpenChange]);

  const handleMenuClose = useCallback(() => {
    setSearchInput('');
    onMenuOpenChange?.(false);
  }, [onMenuOpenChange]);

  return (
    <Box className={cx('report-filter-field')}>
      <span className={cx('report-filter-field-label')}>{t('filters.subject')}</span>
      <BaseSelect<PersonSelectOption>
        className={cx('report-subject-select')}
        id={triggerId}
        options={personOptions}
        value={selectedPersonId ?? ''}
        valueDisplay={selectedLabel}
        displayInvalidValue
        placeholder={t('filters.chooseSubject')}
        isSearchEnable
        isSortEnable={false}
        isDisplayAllOption
        onChange={(e) => {
          const value = String(e.target.value ?? '');
          onSelectedPersonIdChange(value === '' ? null : value);
        }}
        onSearchChange={handleSearchChange}
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
        renderOptionItem={renderPersonOptionRow}
      />
    </Box>
  );
});

export default ReportSubjectFilter;
